import CircularProgress from "@mui/material/CircularProgress";
import { useEffect, useRef, useState } from "react";
import {
  fetchCategoryProductsLevel1,
} from "../../../../../../../../config/Services";
import "./SearchSidebar.css";

/**
 * Pure navigation rail — no product cards.
 * Calls onSelect({ type: "top-searches" | "bundles" | "category", name? })
 * when the user clicks a row.
 */
export default function SearchSidebar({ selected, onSelect }) {
  const orgId = localStorage.getItem("orgId");
  const locId = localStorage.getItem("locId");

  const [categories,  setCategories]  = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);

  const paneRef = useRef(null);

  /* Arrow-key navigation within the sidebar */
  const handleSidebarKeyDown = (e) => {
    if (!paneRef.current) return;
    const btns = Array.from(paneRef.current.querySelectorAll("button"));
    const idx  = btns.indexOf(document.activeElement);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      btns[(idx + 1) % btns.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      btns[(idx - 1 + btns.length) % btns.length]?.focus();
    } else if (e.key === "ArrowRight") {
      /* Return to the search input / results area */
      e.preventDefault();
      document.getElementById("search-input")?.focus();
    }
  };

  /* Category name lives in the `key` field of the level-1 API response */
  const getName = (cat) => {
    if (typeof cat === "string") return cat;
    return cat?.key || "";
  };

  /* ── Fetch category list ── */
  useEffect(() => {
    let cancelled = false;

    fetchCategoryProductsLevel1({
      page: 1,
      pageSize: 100,
      storeLocations: [locId],
      productTypes: [],
      salesChannels: [],
      storeIds: [orgId]
    })
      .then((res) => {
        if (cancelled) return;
        const d = res?.data;
        /* Try every plausible response shape from the CMS category endpoint */
        const list =
          d?.data?.data?.data       ||   // { data: { data: { data: [...] } } }
          d?.data?.data?.results    ||
          d?.data?.data?.categories ||
          d?.data?.categories       ||
          d?.data?.data             ||   // { data: { data: [...] } }
          d?.data?.results          ||
          d?.categories             ||
          (Array.isArray(d?.data) ? d.data : null) ||
          (Array.isArray(d) ? d : null) ||
          [];
        /* Filter out entries with a blank/whitespace-only key */
        setCategories(
          Array.isArray(list)
            ? list.filter((c) => getName(c).trim() !== "")
            : []
        );
      })
      .catch((err) => {
        console.error("[SearchSidebar] categories fetch error:", err);
        if (!cancelled) setCategories([]);
      })
      .finally(() => { if (!cancelled) setLoadingCats(false); });

    return () => { cancelled = true; };
  }, [locId]);

  const isSelected = (type, name) =>
    selected?.type === type && (type !== "category" || selected?.name === name);

  return (
    <aside className="ss-pane" ref={paneRef} onKeyDown={handleSidebarKeyDown}>

      {/* ── Fixed nav items ── */}
      <div className="ss-fixed-nav">
        {/* Top Searches */}
        <button
          type="button"
          tabIndex={-1}
          className={`ss-nav-row${isSelected("top-searches") ? " ss-nav-row--active" : ""}`}
          onClick={() => onSelect({ type: "top-searches" })}
        >
          <span className="ss-nav-icon" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m21 21-4.34-4.34" /><circle cx="11" cy="11" r="8" />
            </svg>
          </span>
          <span className="ss-nav-label">Top Searches</span>
          {isSelected("top-searches") && <span className="ss-nav-active-dot" aria-hidden="true" />}
        </button>

        {/* Bundles */}
        <button
          type="button"
          tabIndex={-1}
          className={`ss-nav-row${isSelected("bundles") ? " ss-nav-row--active" : ""}`}
          onClick={() => onSelect({ type: "bundles" })}
        >
          <span className="ss-nav-icon" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="3.29 7 12 12 20.71 7" /><line x1="12" y1="22" x2="12" y2="12" />
            </svg>
          </span>
          <span className="ss-nav-label">Bundles</span>
          {isSelected("bundles") && <span className="ss-nav-active-dot" aria-hidden="true" />}
        </button>
      </div>

      {/* ── Category list ── */}
      <div className="ss-cat-section">
        <p className="ss-cat-header">Categories</p>

        {loadingCats ? (
          <div className="ss-cat-loading">
            <CircularProgress size={16} sx={{ color: "#2a71ff" }} />
          </div>
        ) : categories?.length === 0 ? (
          <p className="ss-empty">No categories</p>
        ) : (
          <div className="ss-cat-list">
            {categories?.map((cat, i) => {
              const name = getName(cat);
              const active = isSelected("category", name);
              return (
                <button
                  key={cat?.id || cat?._id || i}
                  type="button"
                  tabIndex={-1}
                  className={`ss-nav-row${active ? " ss-nav-row--active" : ""}`}
                  onClick={() => onSelect({ type: "category", name })}
                >
                  <span className="ss-nav-icon ss-nav-icon--dot" aria-hidden="true" />
                  <span className="ss-nav-label">{name}</span>
                  {active && <span className="ss-nav-active-dot" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
}
